/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { ROUTES } from '@sportninja/common/constants/app'
import { t } from '@sportninja/common/i18n'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { Link, Redirect } from 'react-router-dom'

import ErrorScreen from '../../../../components/ErrorScreen'
import Helmet from '../../../../components/Helmet'
import LoadingSpinner from '../../../../components/LoadingSpinner'
import ResendVerifyEmail from '../../../../components/ResendVerifyEmail'
import CanlanTextLogo from '../../../../components/icons/CanlanTextLogo'
import SportNinjaLogo from '../../../../components/ts-components/SportNinjaLogo'
import Text from '../../../../components/ts-components/Text'
import { UnderageInvitation } from '../../../../pages/UnderageInvitation'
import { useInviteSignup } from '../hooks/useInviteSignup'
import InviteContextLine from './InviteContextLine'
import InviteSignupForm from './InviteSignupForm'
import InviteSignupSkeleton from './InviteSignupSkeleton'

type InviteSignupScreenProps = {
  inviteId: string
  isLoggedIn: boolean
  readInvite: (id: string) => Promise<any>
  register: (body: Record<string, unknown>) => Promise<any>
  login: (credentials: { email: string; password: string }) => Promise<any>
  logout: () => Promise<any>
  setNewUserInviteId: (id: string) => void
  redirect: (path: string) => void
}

/**
 * Signup screen for an invitation, at `/invitations/:inviteId/signup` and
 * `/invitations/:inviteId/register`.
 *
 * A single centered column: branding, what the invite is for, the fields, then
 * the link out to logging in instead. The app's nav and sidebar hide themselves
 * on this route, so the screen owns the whole viewport.
 */
const InviteSignupScreen = (props: InviteSignupScreenProps) => {
  const {
    nameFirst,
    nameLast,
    password,
    confirmPassword,
    birthDate,
    setNameFirst,
    setNameLast,
    setPassword,
    setConfirmPassword,
    setBirthDate,
    invite,
    isPlayerInvite,
    loading,
    success,
    pageError,
    shouldPushToUnderageFlow,
    errors,
    fieldErrors,
    passwordsMatch,
    onSubmit,
  } = useInviteSignup(props)

  const { inviteId, login, logout } = props

  if (shouldPushToUnderageFlow) {
    return (
      <UnderageInvitation inviteId={inviteId} login={login} logout={logout} />
    )
  }

  if (pageError) {
    return (
      <>
        <Helmet title='Invitation Unavailable' />
        <ErrorScreen
          title={pageError.title || 'INVITATION UNAVAILABLE'}
          message={
            pageError.message ||
            'This invitation could not be loaded. Please check the link and try again.'
          }
        />
      </>
    )
  }

  /*
   * Canlan's wordmark sizes itself from `height`; SportNinja's renders a fixed
   * 268x60 with no size props, so only that branch needs the scaling wrapper.
   */
  const logo = isCanlan ? (
    <CanlanTextLogo height={32} color={colors.PRIMARY} />
  ) : (
    <div css={styles.sportninjaLogo}>
      <SportNinjaLogo />
    </div>
  )

  return (
    <div css={styles.page}>
      <Helmet title='Invitation: Register' />

      {loading.page ? (
        <InviteSignupSkeleton />
      ) : (
        <div css={styles.column}>
          <div css={styles.header}>
            <div css={styles.logo}>{logo}</div>
            <div css={styles.headerCopy}>
              <Text
                variant='heading2'
                weight='semiBold'
                color={colors.NEUTRAL_0}
                style={'text-align: center;'}
              >
                {capitalizeWords(t('Web:toAcceptInvite', {}))}
              </Text>
              {!success ? (
                <div css={styles.context}>
                  <InviteContextLine invite={invite} />
                </div>
              ) : null}
            </div>
          </div>

          {success ? (
            <InviteSignupSuccess
              success={success}
              email={invite.email}
              inviteId={inviteId}
            />
          ) : (
            <>
              <InviteSignupForm
                nameFirst={nameFirst}
                nameLast={nameLast}
                password={password}
                confirmPassword={confirmPassword}
                birthDate={birthDate}
                setNameFirst={setNameFirst}
                setNameLast={setNameLast}
                setPassword={setPassword}
                setConfirmPassword={setConfirmPassword}
                setBirthDate={setBirthDate}
                errors={errors}
                fieldErrors={fieldErrors}
                loading={loading}
                passwordsMatch={passwordsMatch}
                isPlayerInvite={isPlayerInvite}
                onSubmit={onSubmit}
              />

              <div css={styles.footer}>
                <div css={styles.rule} />
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={colors.NEUTRAL_50}
                  style={'text-align: center;'}
                >
                  {t('Auth:orIfYouAlreadyHaveAnAccount', {})}{' '}
                  <Link
                    css={styles.link}
                    to={`${ROUTES.INVITE_V2_ROOT}${inviteId}`}
                  >
                    {capitalizeWords(t('common:login', {}))}
                  </Link>{' '}
                  to accept the invite.
                </Text>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default InviteSignupScreen

type InviteSignupSuccessProps = {
  success: 'loading' | 'redirect' | 'verification'
  email: string
  inviteId: string
}

/**
 * What replaces the form once the account exists.
 *
 * A spinner is correct here: this is an action in flight (log in, then bounce
 * to the invite), not data arriving, so there is no layout to preview.
 */
const InviteSignupSuccess = ({
  success,
  email,
  inviteId,
}: InviteSignupSuccessProps) => {
  if (success === 'verification') {
    return (
      <div css={styles.success}>
        <Text
          variant='paragraphMedium'
          weight='regular'
          color={colors.NEUTRAL_0}
        >
          {`${t('Web:thanksForRegistering', {})} ${t(
            'Web:inviteRegisterVerify',
            { email }
          )}.`}
        </Text>
        <Text
          variant='paragraphSmall'
          weight='regular'
          color={colors.NEUTRAL_50}
        >
          {t('Web:pleaseClickButtonInEmail', {})}
        </Text>
        <Text
          variant='paragraphSmall'
          weight='regular'
          color={colors.NEUTRAL_50}
        >
          {t('Web:ifYouCantFindItHint', {})}
        </Text>
        <ResendVerifyEmail email={email} />
      </div>
    )
  }

  return (
    <div css={styles.success}>
      <Text variant='paragraphMedium' weight='regular' color={colors.NEUTRAL_0}>
        {`${t('Web:thanksForRegistering', {})} ${
          success === 'redirect'
            ? t('Web:inviteRegisterRedirect', {})
            : t('Web:giveUsAMoment', {})
        }.`}
      </Text>
      {/* `size` is in em, not px — 3em is roughly a 48px circle. */}
      <LoadingSpinner size={3} />
      {success === 'redirect' ? (
        <Redirect to={`${ROUTES.INVITE_V2_ROOT}${inviteId}`} />
      ) : null}
    </div>
  )
}

const styles = {
  /*
   * Centered both ways. `align-items: center` with `min-height: 100vh` holds
   * the form in the middle of the viewport, and the padding keeps it off the
   * edges once the content grows taller than the screen.
   */
  page: css`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    padding: 16px;
    padding-block: 48px;
    background: ${colors.SECONDARY_400};
  `,
  column: css`
    display: flex;
    flex-direction: column;
    gap: 32px;
    width: 100%;
    max-width: 650px;
  `,
  /*
   * 32px under the logo, 16px between title and context — the wider gap sets
   * the branding apart from the copy it introduces.
   */
  header: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 32px;
  `,
  headerCopy: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
  `,
  logo: css`
    display: flex;
    align-items: center;
    justify-content: center;
    max-width: 100%;
  `,
  /*
   * SportNinjaLogo renders a fixed 268x60 SVG with no size props, so scale it
   * from the outside rather than changing the shared component for one caller.
   */
  sportninjaLogo: css`
    display: flex;

    svg {
      height: 48px;
      width: auto;
      max-width: 100%;
    }
  `,
  context: css`
    text-align: center;
  `,
  footer: css`
    display: flex;
    flex-direction: column;
    gap: 16px;
  `,
  rule: css`
    height: 1px;
    background: ${colors.SECONDARY_300};
  `,
  link: css`
    color: ${colors.PRIMARY};
    text-decoration: underline;
    text-underline-offset: 2px;
  `,
  success: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    text-align: center;
  `,
}
